import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';

/**
 * The thing you type into. Auto-growing textarea, Enter-to-send,
 * Shift+Enter for a newline, and the optional ChatGPT-style typewriter
 * placeholder.
 *
 * Extracted because it was the same thirty lines three times inside
 * ChatWidget — the compact/expanded card, the offer-ingest overlay and the
 * chat-draft overlay — and a fourth chat (the Knowledge Base screen's Ask
 * tab) would have made it four. It owns the draft text: nothing above it
 * needs a keystroke, only the finished turn.
 */

// Max auto-grown height before the textarea starts scrolling internally.
const MAX_TEXTAREA_HEIGHT = 200;

/**
 * Small chained-timeout typewriter. Types one example, holds, deletes, moves
 * to the next. Runs only while `active` — gate on an empty, unfocused field
 * so a real keystroke freezes the effect without clearing what was typed.
 */
export function useTypedPlaceholder(examples, active) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!active || examples.length === 0) {
      setText('');
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let idx = 0;
    let charIdx = 0;
    let phase = 'typing';
    const schedule = (ms) => {
      timer = setTimeout(tick, ms); // eslint-disable-line no-use-before-define
    };
    const tick = () => {
      if (cancelled) return;
      const current = examples[idx];
      if (phase === 'typing') {
        charIdx += 1;
        setText(current.slice(0, charIdx));
        if (charIdx >= current.length) { phase = 'holding'; schedule(1600); return; }
        schedule(55);
      } else if (phase === 'holding') {
        phase = 'deleting';
        schedule(400);
      } else if (phase === 'deleting') {
        charIdx -= 1;
        setText(current.slice(0, Math.max(0, charIdx)));
        if (charIdx <= 0) {
          idx = (idx + 1) % examples.length;
          charIdx = 0;
          phase = 'typing';
          schedule(350);
          return;
        }
        schedule(25);
      }
    };
    schedule(400);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [examples, active]);
  return text;
}

const NO_EXAMPLES = [];

function ChatComposer({
  onSubmit,
  disabled = false,
  pending = false,
  rows = 1,
  placeholder = 'Ask the assistant…',
  examples = NO_EXAMPLES,
  // Anything whose change resizes the box the textarea sits in (the widget's
  // compact↔expanded switch). Re-measures without the composer having to know
  // what changed.
  resizeKey = null,
  label = 'Message',
}) {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef(null);

  // Auto-grow: shrink to content, cap at MAX_TEXTAREA_HEIGHT.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [input, resizeKey]);

  const typed = useTypedPlaceholder(
    examples,
    examples.length > 0 && !focused && input.length === 0 && !disabled,
  );

  const canSend = !disabled && input.trim().length > 0;

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!canSend) return;
    onSubmit(input);
    setInput('');
  };

  return (
    <Form onSubmit={handleSubmit} className="chat-widget-input">
      <div className="chat-widget-input-box">
        <Form.Control
          as="textarea"
          ref={textareaRef}
          rows={rows}
          aria-label={label}
          placeholder={typed ? `${typed}|` : placeholder}
          value={input}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          disabled={disabled}
        />
        <button
          type="submit"
          className="chat-widget-send-button"
          disabled={!canSend}
          aria-label="Send"
          title="Send"
        >
          {pending ? (
            <i className="fa fa-spinner fa-spin" aria-hidden="true" />
          ) : (
            <i className="fa fa-arrow-up" aria-hidden="true" />
          )}
        </button>
      </div>
    </Form>
  );
}

export default ChatComposer;
